import { useTranslation } from "@outschool/localization";
import { gql, useMutation } from "@outschool/ui-apollo";
import shuffle from "lodash/shuffle";
import { useMemo, useState } from "react";

const trackUserAttribution = gql`
  # This mutation is used to track the user's response to the attribution survey.
  mutation TrackUserAttributionSurveyResponse(
    $input: TrackUserAttributionSurveyResponseInput!
  ) {
    trackUserAttributionSurveyResponse(input: $input)
  }
`;

const useAttributionSurvey = ({ onComplete }: $TSFixMe) => {
  const { t } = useTranslation(
    "client\\components\\enrolling\\HowDidYouHearAboutUsModal",
  );

  const [trackSurveyResponse] = useMutation(trackUserAttribution);

  const [selectedOption, setSelectedOption] = useState("");

  const [additionalDetails, setAdditionalDetails] = useState("");

  const handleSurveyCompletion = async () => {
    trackSurveyResponse({
      variables: {
        input: {
          response: selectedOption,
          additionalDetails,
        },
      },
    }).then(onComplete);
  };

  const handleSkipOption = async () => {
    trackSurveyResponse({
      variables: {
        input: {
          response: "skip",
        },
      },
    }).then(onComplete);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAdditionalDetails("");
    setSelectedOption(event.target.value);
  };

  const handleAdditionalDetailsChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setAdditionalDetails(event.target.value);
  };

  const orderedOptions = useMemo(() => {
    const options = [
      { value: "friend", label: t("Friend") },
      { value: "facebook", label: t("Facebook ad") },
      { value: "instagram", label: t("Instagram ad") },
      { value: "tiktok", label: t("TikTok ad") },
      { value: "youtube", label: t("YouTube ad") },
      { value: "search", label: t("Search (e.g. Google, Bing, Naver)") },
      {
        value: "pressArticleOrWebsite",
        label: t("News article or blog post"),
        shouldGetAdditionalDetails: true,
        additionalDetailsPlaceholder: t("Which one?"),
      },
      {
        value: "podcast",
        label: t("Podcast"),
        shouldGetAdditionalDetails: true,
        additionalDetailsPlaceholder: t("Which podcast?"),
      },
      {
        value: "influencer",
        label: t("Sponsored post from someone I follow"),
        shouldGetAdditionalDetails: true,
        additionalDetailsPlaceholder: t("Please tell us who"),
      },
      {
        value: "community",
        label: t("Online group, forum, or community"),
        shouldGetAdditionalDetails: true,
        additionalDetailsPlaceholder: t("Which one?"),
      },
      {
        value: "chatGptOrAiTool",
        label: t("ChatGPT / AI Tool"),
      },
      {
        value: "school",
        label: t("School"),
        shouldGetAdditionalDetails: true,
        additionalDetailsPlaceholder: t("Which one?"),
      },
    ];

    const shuffled = shuffle([...options]);
    return [
      ...shuffled,
      {
        value: "other",
        label: t("Other"),
        shouldGetAdditionalDetails: true,
        additionalDetailsPlaceholder: t("Please tell us more"),
      },
    ];
  }, [t]);

  return {
    selectedOption,
    handleChange,
    additionalDetails,
    handleAdditionalDetailsChange,
    orderedOptions,
    handleSkipOption,
    handleSurveyCompletion,
  };
};

export default useAttributionSurvey;
